import React, { useEffect, useState } from 'react';
import type { IconType } from 'react-icons';
import { LuSparkle } from 'react-icons/lu';

import { CalendarSidebarPanel } from '@/components/CalendarSidebarPanel';
import { Sidebar } from '@/components/Sidebar';
import { cn } from '@/utils/cn';
import { SidebarContent, useAiChatSidebarState } from '@/views/ai-chat/ai-chat-state';
import { AiChatSidebar } from '@/views/ai-chat/AiChatSidebar';
import { useDockState } from '@/views/dock/dock-state';

import { CalendarIcon } from './Icons';

const SIDEBAR_DEFAULT_WIDTH = 620;
const SIDEBAR_MIN_WIDTH = 380;
const SIDEBAR_MAX_WIDTH = 1200;

interface SidebarSelectorItem {
    icon: IconType;
    label: string;
    value: SidebarContent;
}

const SIDEBAR_SELECTOR_ITEMS: SidebarSelectorItem[] = [
    { value: 'sidekick', label: 'Sidekick', icon: LuSparkle },
    { value: 'calendar', label: 'Calendar', icon: CalendarIcon },
];

interface SidebarContentSelectorBarProps {
    onSelect: (content: SidebarContent) => void;
    selectedContent: SidebarContent;
}

const SidebarContentSelectorBar: React.FC<SidebarContentSelectorBarProps> = ({ onSelect, selectedContent }) => {
    return (
        <div className="flex h-[70px] shrink-0 items-center px-6">
            <div className="bg-cocoa-0 mx-auto flex h-[40px] w-fit items-center gap-1 rounded-full p-1">
                {SIDEBAR_SELECTOR_ITEMS.map(({ icon: Icon, label, value }) => {
                    const isActive = value === selectedContent;

                    return (
                        <button
                            key={value}
                            type="button"
                            aria-label={label}
                            aria-pressed={isActive}
                            title={label}
                            className={cn(
                                'flex h-8 w-8 cursor-pointer items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
                                isActive
                                    ? 'bg-[#433938] text-white shadow-sm'
                                    : 'text-[#897E7B] hover:bg-white/80 hover:text-[#5A4F4E]',
                            )}
                            onClick={() => onSelect(value)}
                        >
                            <Icon className="h-4 w-4" />
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export const AppSidebar: React.FC = () => {
    const { isOpen, selectedContent, setSelectedContent, sidebarWidth, setWidth } = useAiChatSidebarState();
    const { activeId: dockActiveId } = useDockState();
    const [isModalOpen, setIsModalOpen] = useState(() => document.body.dataset.scrollLocked === '1');

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsModalOpen(document.body.dataset.scrollLocked === '1');
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ['data-scroll-locked'] });
        return () => observer.disconnect();
    }, []);

    const normalizedSelectedContent = SIDEBAR_SELECTOR_ITEMS.some((item) => item.value === selectedContent)
        ? selectedContent
        : 'sidekick';
    const isSidekickSelected = normalizedSelectedContent === 'sidekick';

    return (
        <Sidebar
            isOpen={isOpen}
            defaultWidth={SIDEBAR_DEFAULT_WIDTH}
            initialWidth={sidebarWidth}
            minWidth={SIDEBAR_MIN_WIDTH}
            maxWidth={SIDEBAR_MAX_WIDTH}
            onWidthChange={setWidth}
            disabled={isModalOpen || dockActiveId !== null}
            contentClassName="relative flex min-w-0"
        >
            <div className="flex min-w-0 flex-1 flex-col bg-white">
                <SidebarContentSelectorBar selectedContent={normalizedSelectedContent} onSelect={setSelectedContent} />
                <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
                    <div
                        className={cn('min-h-0 flex-1', !isSidekickSelected && 'hidden')}
                        aria-hidden={!isSidekickSelected}
                    >
                        <AiChatSidebar />
                    </div>
                    {normalizedSelectedContent === 'calendar' ? <CalendarSidebarPanel /> : null}
                </div>
            </div>
        </Sidebar>
    );
};
